import { useTenant } from "@/hooks/useTenant";
import { portalFetch } from "@/lib/portalApi";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export interface UserGroup {
  id: string;
  tenantId: string;
  name: string;
  createdAt: string;
}

export interface GroupMember {
  id: string;
  groupId: string;
  userId: string;
  email: string;
  name: string | null;
  createdAt: string;
}

// ─── Queries ─────────────────────────────────────────────────────────────────

export function useUserGroups() {
  const { currentTenantId } = useTenant();

  const { data, isLoading } = useQuery({
    queryKey: ["portal-groups", currentTenantId],
    queryFn: () => portalFetch<UserGroup[]>(`/api/portal/tenants/${currentTenantId}/groups`),
    enabled: !!currentTenantId,
  });

  return { data: data ?? [], isLoading };
}

export function useGroupMembers(groupId: string | null) {
  const { currentTenantId } = useTenant();

  const { data, isLoading } = useQuery({
    queryKey: ["portal-group-members", currentTenantId, groupId],
    queryFn: () =>
      portalFetch<GroupMember[]>(
        `/api/portal/tenants/${currentTenantId}/groups/${groupId}/members`,
      ),
    enabled: !!currentTenantId && !!groupId,
  });

  return { data: data ?? [], isLoading };
}

export function useGroupDashboardIds(groupId: string | null) {
  const { currentTenantId } = useTenant();

  const { data, isLoading } = useQuery({
    queryKey: ["portal-group-dashboards", currentTenantId, groupId],
    queryFn: async () => {
      const assigns = await portalFetch<{ id: string; groupId: string; dashboardId: string }[]>(
        `/api/portal/tenants/${currentTenantId}/groups/${groupId}/dashboards`,
      );
      return assigns.map((a) => a.dashboardId);
    },
    enabled: !!currentTenantId && !!groupId,
  });

  return { data: data ?? [], isLoading };
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function useCreateGroup() {
  const { currentTenantId } = useTenant();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (name: string) =>
      portalFetch<UserGroup>(`/api/portal/tenants/${currentTenantId}/groups`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenantId: currentTenantId, name }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["portal-groups"] }),
  });
}

/**
 * Rename a group in place (SIM-2553). Name-only: membership, dashboard
 * assignments, and the tenant's default-group setting all key off the group id,
 * so nothing else needs invalidating.
 */
export function useRenameGroup() {
  const { currentTenantId } = useTenant();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (input: { groupId: string; name: string }) =>
      portalFetch<UserGroup>(`/api/portal/tenants/${currentTenantId}/groups/${input.groupId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenantId: currentTenantId, id: input.groupId, name: input.name }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["portal-groups"] }),
  });
}

export function useDeleteGroup() {
  const { currentTenantId } = useTenant();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (groupId: string) =>
      portalFetch(`/api/portal/tenants/${currentTenantId}/groups/${groupId}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenantId: currentTenantId, id: groupId }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["portal-groups"] });
      void qc.invalidateQueries({ queryKey: ["portal-group-members"] });
    },
  });
}

export function useAddGroupMember() {
  const { currentTenantId } = useTenant();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (input: { groupId: string; userId: string }) =>
      portalFetch<GroupMember>(
        `/api/portal/tenants/${currentTenantId}/groups/${input.groupId}/members`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tenantId: currentTenantId, ...input }),
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["portal-group-members"] }),
  });
}

export function useRemoveGroupMember() {
  const { currentTenantId } = useTenant();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (input: { groupId: string; userId: string }) =>
      portalFetch(
        `/api/portal/tenants/${currentTenantId}/groups/${input.groupId}/members/${input.userId}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tenantId: currentTenantId, ...input }),
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["portal-group-members"] }),
  });
}

export function useAssignGroupDashboard() {
  const { currentTenantId } = useTenant();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (input: { dashboardId: string; groupId: string }) =>
      portalFetch(
        `/api/portal/tenants/${currentTenantId}/dashboards/${input.dashboardId}/assign-group`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tenantId: currentTenantId, ...input }),
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["portal-group-dashboards"] });
      void qc.invalidateQueries({ queryKey: ["portal-dashboards"] });
    },
  });
}

export function useUnassignGroupDashboard() {
  const { currentTenantId } = useTenant();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (input: { dashboardId: string; groupId: string }) =>
      portalFetch(
        `/api/portal/tenants/${currentTenantId}/dashboards/${input.dashboardId}/assign-group/${input.groupId}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tenantId: currentTenantId, ...input }),
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["portal-group-dashboards"] });
      void qc.invalidateQueries({ queryKey: ["portal-dashboards"] });
    },
  });
}
